import React, {useState, useEffect} from 'react';
import {useForm, useFieldArray, Controller} from 'react-hook-form';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Grid,
    Box,
    Chip,
    Switch,
    FormHelperText,
    Divider
} from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import {makeStyles} from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import {useSnackbar} from 'notistack';
import API_Service from '../../Services/API_Service';
import {req_survey_form_create, req_survey_form_update} from '../../Helper/Constants';
import DateRangePickerModal from '../DateRangePickerModal';
import OutletChainSelector from '../OutletChainSelector';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    dialog: {
        minWidth: 600,
    },
    sectionTitle: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.main,
        fontWeight: 'bold'
    },
    questionCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.background.paper
    },
    questionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2)
    },
    optionContainer: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1)
    },
    deleteButton: {
        color: theme.palette.error.main
    },
    addButton: {
        marginTop: theme.spacing(1)
    },
    previewSection: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.spacing(1),
        border: `1px dashed ${theme.palette.divider}`
    },
    previewTitle: {
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1)
    },
    compulsoryCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        marginTop: theme.spacing(2)
    },
    compulsoryHint: {
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5)
    }
}));

const questionTypes = [
    {value: 'text', label: 'Text Input'},
    {value: 'textarea', label: 'Long Text'},
    {value: 'single_choice', label: 'Single Choice (Radio)'},
    {value: 'multiple_choice', label: 'Multiple Choice (Checkbox)'},
    {value: 'dropdown', label: 'Dropdown'},
    {value: 'rating', label: 'Rating Scale'},
    {value: 'file_upload', label: 'File Upload'},
    {value: 'date', label: 'Date Picker'},
    {value: 'email', label: 'Email'},
    {value: 'number', label: 'Number'}
];

const statusOptions = [
    {value: 'draft', label: 'Draft'},
    {value: 'active', label: 'Active'},
    {value: 'closed', label: 'Closed'}
];

export default function SurveyFormBuilder({gState, open, onClose, onSubmit, axiosConfig, language, editData = null}) {
    const classes = useStyles();
    const {enqueueSnackbar} = useSnackbar();
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingData, setPendingData] = useState(null);
    const [openDatePicker, setOpenDatePicker] = useState(false);
    const [openOutletSelector, setOpenOutletSelector] = useState(false);
    const [dateRange, setDateRange] = useState({startDate: null, endDate: null});
    const [outletSelection, setOutletSelection] = useState({appliesToAll: false, chainIds: [], outletIds: [], outletChainMap: {}});

    const {
        control,
        handleSubmit,
        reset,
        watch,
        errors,
        setValue,
        getValues
    } = useForm({
        defaultValues: {
            title: '',
            description: '',
            status: 'draft',
            is_compulsory: false,
            questions: [{
                question_text: '',
                question_type: 'text',
                is_required: false,
                options: '',
                validation_rules: {}
            }]
        }
    });

    const {
        fields: questions,
        append: appendQuestion,
        remove: removeQuestion
    } = useFieldArray({
        control,
        name: 'questions'
    });

    const watchedQuestions = watch('questions');
    const watchedStatus = watch('status');
    
    // 检查是否应该禁用问题编辑功能（当editData存在且status为active时）
    const isQuestionsDisabled = editData && (editData.status === 'active' || watchedStatus === 'active');

    useEffect(() => {
        if (editData) {
            const formattedQuestions = (editData.questions || []).map(question => ({
                ...question,
                is_required: question.is_required === "1" || question.is_required === 1 || question.is_required === true,
                options: question.options
            }));
            
            reset({
                title: editData.title || '',
                description: editData.description || '',
                status: editData.status || 'draft',
                is_compulsory: editData.is_compulsory === "1" || editData.is_compulsory === 1 || editData.is_compulsory === true,
                questions: formattedQuestions.length > 0 ? formattedQuestions : [{
                    question_text: '',
                    question_type: 'text',
                    is_required: false,
                    options: '',
                    validation_rules: {}
                }]
            });

            setDateRange({
                startDate: editData.active_date_from ? new Date(editData.active_date_from) : null,
                endDate: editData.active_date_to ? new Date(editData.active_date_to) : null,
                key: 'selection'
            });
            setOutletSelection({
                appliesToAll: `${editData.applies_to_all_outlets}` === '1',
                chainIds: Array.isArray(editData.chain_ids) ? editData.chain_ids.map(Number) : [],
                outletIds: Array.isArray(editData.outlet_ids) ? editData.outlet_ids.map(Number) : [],
                outletChainMap: editData.outlet_chain_map || {}
            });
        }
    }, [editData, reset]);

    const addQuestion = () => {
        appendQuestion({
            question_text: '',
            question_type: 'text',
            is_required: false,
            options: '',
            validation_rules: {}
        });
    };

    // 处理表单数据提交到API的函数
    const submitFormData = async (data) => {
        try {
            let formData = new FormData();

            formData.append('title', data.title);
            formData.append('description', data.description);
            formData.append('status', data.status);
            formData.append('is_compulsory', data.is_compulsory ? 1 : 0);

            if (editData) {
                formData.append('survey_id', editData.id);
                formData.append('updated_by', gState.username);
            } else {
                formData.append('created_by', gState.username);
            }

            const fmtDate = (d) => d ? moment(d).format('YYYY-MM-DD') : '';
            formData.append('active_date_from', fmtDate(dateRange.startDate));
            formData.append('active_date_to', fmtDate(dateRange.endDate));
            const appliesAll = outletSelection.appliesToAll
                || ((outletSelection.chainIds || []).length === 0 && (outletSelection.outletIds || []).length === 0);
            formData.append('applies_to_all_outlets', appliesAll ? '1' : '0');
            if (!appliesAll) {
                (outletSelection.chainIds || []).forEach((id) => formData.append('chain_ids[]', id));
                (outletSelection.outletIds || []).forEach((id) => formData.append('outlet_ids[]', id));
            }

            data.questions.forEach((question, index) => {
                formData.append(`questions[${index}][question_text]`, question.question_text);
                formData.append(`questions[${index}][question_type]`, question.question_type);
                formData.append(`questions[${index}][is_required]`, question.is_required);
                formData.append(`questions[${index}][sort_order]`, index);

                // 如果是编辑模式且question有id，则传递question_id
                if (editData && question.id) {
                    formData.append(`questions[${index}][question_id]`, question.id);
                }

                if (question.question_type === 'single_choice' || question.question_type === 'multiple_choice' || question.question_type === 'dropdown') {
                    // 如果options是字符串，需要解析成数组
                    const optionsArray = typeof question.options === 'string' 
                        ? question.options
                        : question.options.filter(opt => opt.trim() !== '');
                    formData.append(`questions[${index}][options]`, optionsArray);
                }
            });

            const apiEndpoint = editData ? req_survey_form_update : req_survey_form_create;
            
            API_Service.post(apiEndpoint, formData, axiosConfig).then(({data}) => {
				const {result, extraData} = data;
                if (result === 1) {
                    enqueueSnackbar(editData ? 'Survey updated successfully' : 'Survey created successfully', {variant: 'success'});
                    handleClose();
                    onSubmit();
                } else {
                    enqueueSnackbar(editData ? 'Failed to update survey' : 'Failed to create survey', {variant: 'error'});
                }
			});
        } catch (error) {
            console.error(editData ? 'Update survey error:' : 'Create survey error:', error);
            enqueueSnackbar(editData ? 'Error updating survey' : 'Error creating survey', {variant: 'error'});
        }
    };

    // 表单提交处理函数
    const onFormSubmit = async (data) => {
        if(data.status === 'active') {
            // 使用对话框替代window.confirm
            setPendingData(data);
            setConfirmDialogOpen(true);
        } else {
            // 如果不是发布状态，直接提交
            submitFormData(data);
        }
    };

    const handleClose = () => {
        reset();
        setDateRange({startDate: null, endDate: null});
        setOutletSelection({appliesToAll: false, chainIds: [], outletIds: [], outletChainMap: {}});
        setOpenDatePicker(false);
        setOpenOutletSelector(false);
        onClose();
    };

    const dateSummaryText = () => {
        if (dateRange.startDate && dateRange.endDate) {
            return `${moment(dateRange.startDate).format('YYYY-MM-DD')} → ${moment(dateRange.endDate).format('YYYY-MM-DD')}`;
        }
        return '';
    };

    const outletSummaryText = () => {
        const c = (outletSelection.chainIds || []).length;
        const o = (outletSelection.outletIds || []).length;
        if (outletSelection.appliesToAll || (c === 0 && o === 0)) {
            return language.all_outlets || 'All Outlets';
        }
        const parts = [];
        if (c > 0) {
            parts.push(`${c} ${language.unit_chains || 'chains'}`);
        }
        if (o > 0) {
            parts.push(`${o} ${language.unit_outlets || 'outlets'}`);
        }
        return `${parts.join(', ')} ${language.selected || 'selected'}`;
    };

    const isChoiceQuestion = (questionType) => {
        return ['single_choice', 'multiple_choice', 'dropdown'].includes(questionType);
    };
    
    // 添加问题预览组件
    const QuestionPreview = ({ question }) => {
        const classes = useStyles();
        
        // 根据问题类型渲染不同的预览组件
        const renderPreviewByType = () => {
            if (!question || !question.question_text) {
                return <Typography variant="body2"></Typography>;
            }
            
            switch(question.question_type) {
                case 'text':
                    return (
                        <>
                            <TextField 
                                fullWidth
                                variant="standard" 
                                label={question.question_text}
                                disabled 
                                required={question.is_required} 
                                InputProps={{
                                    disableUnderline: true,
                                }}
                            />
                            <TextField 
                                fullWidth
                                variant="standard" 
                                label={null}
                                placeholder="Enter text here ..." 
                                disabled 
                            />
                        </>
                    )
                case 'textarea':
                    return (
                    <>
                        <TextField 
                            fullWidth
                            variant="standard" 
                            label={question.question_text}
                            disabled
                            required={question.is_required} 
                            InputProps={{
                                disableUnderline: true,
                            }}
                        />
                        <TextField 
                            fullWidth 
                            multiline 
                            rows={3} 
                            label={null} 
                            placeholder="Please fill in ..." 
                            disabled 
                        />
                    </>
                )
                case 'single_choice':
                    return (
                        <FormControl component="fieldset" required={question.is_required}>
                            <Typography variant="body2">{question.question_text}</Typography>
                            {question.options && question.options.split(';').map((option, i) => (
                                <div key={i} style={{ marginTop: 8 }}>
                                    <input type="radio" disabled /> {option.trim()}
                                </div>
                            ))}
                        </FormControl>
                    );
                case 'multiple_choice':
                    return (
                        <FormControl component="fieldset" required={question.is_required}>
                            <Typography variant="body2">{question.question_text}</Typography>
                            {question.options && question.options.split(';').map((option, i) => (
                                <div key={i} style={{ marginTop: 8 }}>
                                    <input type="checkbox" disabled /> {option.trim()}
                                </div>
                            ))}
                        </FormControl>
                    );
                case 'dropdown':
                    return (
                        <FormControl fullWidth required={question.is_required}>
                            <InputLabel id={`dropdown-label-${question.id}`}>{question.question_text}</InputLabel>
                            <br />
                            <Select
                                labelId={`dropdown-label-${question.id}`}
                                // disabled
                                value={question.options?.split(';')[0] || ''}
                            >
                                {question.options && question.options.split(';').map((option, i) => (
                                    <MenuItem key={i} value={option.trim()}>{option.trim()}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    );
                case 'rating':
                    return (
                        <div>
                            <Typography variant="body2">{question.question_text}</Typography>
                            <div style={{ marginTop: 8 }}>
                                <Rating 
                                    name={`preview-rating-${question.id || 'default'}`}
                                    value={3}
                                    readOnly
                                />
                            </div>
                        </div>
                    );
                case 'file_upload':
                    return (
                        <div>
                            <Typography variant="body2">{question.question_text}</Typography>
                            <Button 
                                variant="outlined" 
                                component="label" 
                                disabled 
                                style={{ marginTop: 8 }}
                            >
                                Choose File
                            </Button>
                        </div>
                    );
                case 'date':
                    return <TextField 
                        fullWidth 
                        label={question.question_text} 
                        type="date" 
                        disabled 
                        InputLabelProps={{ shrink: true }}
                        required={question.is_required} 
                    />;
                case 'email':
                    return <TextField 
                        fullWidth 
                        label={question.question_text} 
                        placeholder="example@email.com" 
                        disabled 
                        required={question.is_required} 
                    />;
                case 'number':
                    return <TextField 
                        fullWidth 
                        label={question.question_text} 
                        type="number" 
                        disabled 
                        required={question.is_required} 
                    />;
                default:
                    return <Typography>Please Select Question Type</Typography>;
            }
        };
        
        return (
            <div className={classes.previewSection}>
                <Typography className={classes.previewTitle}>Preview :</Typography>
                {renderPreviewByType()}
            </div>
        );
    };

    // 确认对话框组件
    const ConfirmDialog = () => (
        <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
            <DialogTitle>Confirm Publish</DialogTitle>
            <DialogContent>
                <Typography>Once you publish this survey, it cannot be modified. Are you sure you want to continue?</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setConfirmDialogOpen(false)} color="secondary">
                    Cancel
                </Button>
                <Button 
                    onClick={() => {
                        setConfirmDialogOpen(false);
                        if (pendingData) {
                            submitFormData(pendingData);
                            setPendingData(null);
                        }
                    }} 
                    color="primary" 
                    variant="contained"
                >
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <>
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            classes={{paper: classes.dialog}}
        >
            <DialogTitle>
                {editData ? 'Edit Survey' : 'Create New Survey'}
            </DialogTitle>
            
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <DialogContent>
                    {/* Basic Survey Information */}
                    <Typography variant="h6" className={classes.sectionTitle}>
                        Survey Information
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <Controller
                                name="title"
                                control={control}
                                rules={{required: 'Title is required'}}
                                render={({value, onChange}) => (
                                    <TextField
                                        value={value}
                                        onChange={onChange}
                                        fullWidth
                                        label="Title"
                                        error={!!errors.title}
                                        helperText={errors.title?.message}
                                    />
                                )}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <Controller
                                name="status"
                                control={control}
                                render={({value, onChange}) => (
                                    <FormControl fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select value={value} onChange={onChange}>
                                            {statusOptions.map((status) => (
                                                <MenuItem key={status.value} value={status.value}>
                                                    {status.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={language.active_date_range || 'Active Date Range'}
                                value={dateSummaryText()}
                                placeholder={language.no_date_limit || 'No date limit (always active)'}
                                onClick={() => setOpenDatePicker(true)}
                                InputProps={{readOnly: true}}
                                InputLabelProps={{shrink: true}}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label={language.outlet_visibility || 'Outlet Visibility'}
                                value={outletSummaryText()}
                                onClick={() => setOpenOutletSelector(true)}
                                InputProps={{readOnly: true}}
                                InputLabelProps={{shrink: true}}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                name="description"
                                control={control}
                                render={({value, onChange}) => (
                                    <TextField
                                        value={value}
                                        onChange={onChange}
                                        fullWidth
                                        multiline
                                        rows={3}
                                        label="Description"
                                        // placeholder="Describe the purpose of this survey..."
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    <Box className={classes.compulsoryCard}>
                        <Box>
                            <Typography variant="subtitle2">{language.compulsory}</Typography>
                            <Typography className={classes.compulsoryHint}>
                                {language.compulsory_survey_hint}
                            </Typography>
                        </Box>
                        <Controller
                            name="is_compulsory"
                            control={control}
                            render={({value, onChange}) => (
                                <Switch
                                    checked={value || false}
                                    onChange={(e) => onChange(e.target.checked)}
                                    color="primary"
                                />
                            )}
                        />
                    </Box>

                    {/* Questions Section */}
                    <Typography variant="h6" className={classes.sectionTitle}>
                        Questions
                    </Typography>

                    {questions.map((question, questionIndex) => (
                        <Box key={question.id} className={classes.questionCard}>
                            <div className={classes.questionHeader}>
                                <Typography variant="subtitle1" color="primary">
                                    Question {questionIndex + 1}
                                </Typography>
                                {questions.length > 1 && (
                                    <IconButton
                                        onClick={() => removeQuestion(questionIndex)}
                                        className={classes.deleteButton}
                                        size="small"
                                        disabled={isQuestionsDisabled}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </div>

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={8}>
                                    <Controller
                                        name={`questions[${questionIndex}].question_text`}
                                        control={control}
                                        rules={{required: 'Question text is required'}}
                                        render={({value, onChange}) => (
                                            <TextField
                                                value={value}
                                                onChange={onChange}
                                                fullWidth
                                                label="Question Text *"
                                                error={!!errors.questions?.[questionIndex]?.question_text}
                                                helperText={errors.questions?.[questionIndex]?.question_text?.message}
                                                disabled={isQuestionsDisabled}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Controller
                                        name={`questions[${questionIndex}].question_type`}
                                        control={control}
                                        render={({value, onChange}) => (
                                            <FormControl fullWidth>
                                                <InputLabel>Question Type</InputLabel>
                                                <Select value={value} onChange={onChange} disabled={isQuestionsDisabled}>
                                                    {questionTypes.map((type) => (
                                                        <MenuItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>

                                {/* Choice Options */}
                                {isChoiceQuestion(watchedQuestions[questionIndex]?.question_type) && (
                                    <Grid item xs={12}>
                                        <Controller
                                            name={`questions[${questionIndex}].options`}
                                            control={control}
                                            render={({value, onChange}) => (
                                                <TextField
                                                    value={value || ''}
                                                    onChange={onChange}
                                                    fullWidth
                                                    label="Options"
                                                    placeholder="Enter options separated by ; (e.g. Yes;No;Maybe)"
                                                    multiline
                                                    // rows={2}
                                                    helperText="Enter options separated by ; (e.g. Yes;No;Maybe)" 
                                                    disabled={isQuestionsDisabled}
                                                />
                                            )}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Controller
                                        name={`questions[${questionIndex}].is_required`}
                                        control={control}
                                        render={({value, onChange}) => (
                                            <Box>
                                                <Chip
                                                    label={value ? "Required" : "Optional"}
                                                    color={value ? "primary" : "default"}
                                                    onClick={isQuestionsDisabled ? undefined : () => onChange(!value)}
                                                    variant={value ? "default" : "outlined"}
                                                    clickable={!isQuestionsDisabled}
                                                    disabled={isQuestionsDisabled}
                                                />
                                            </Box>
                                        )}
                                    />
                                </Grid>
                            </Grid>
                            
                            {/* 添加问题预览区域 */}
                            <QuestionPreview question={watchedQuestions[questionIndex]} />
                        </Box>
                    ))}

                    <Button
                        startIcon={<AddIcon />}
                        onClick={addQuestion}
                        variant="outlined"
                        color="primary"
                        fullWidth
                        className={classes.addButton}
                        disabled={isQuestionsDisabled}
                    >
                        Add Question
                    </Button>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} color="secondary">
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        color="primary" 
                        variant="contained"
                    >
                        {editData ? 'Update' : 'Create'} Survey
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
        <ConfirmDialog />
        <DateRangePickerModal
            open={openDatePicker}
            input={dateRange.startDate && dateRange.endDate ? dateRange : {}}
            positive={language.done || 'DONE'}
            negative={language.cancel || 'CANCEL'}
            reset={language.reset_selection || 'RESET'}
            setPositive={(range) => {
                if (range && range.startDate && range.endDate) {
                    setDateRange({startDate: range.startDate, endDate: range.endDate, key: 'selection'});
                } else {
                    setDateRange({startDate: null, endDate: null});
                }
                setOpenDatePicker(false);
            }}
            setNegative={() => setOpenDatePicker(false)}
            setClose={() => setOpenDatePicker(false)}
        />
        <OutletChainSelector
            open={openOutletSelector}
            onClose={() => setOpenOutletSelector(false)}
            onConfirm={(selection) => {
                setOutletSelection(selection);
                setOpenOutletSelector(false);
            }}
            value={outletSelection}
            axiosConfig={axiosConfig}
            language={language}
        />
        </>
    );
}